import { useEffect, useMemo, useState } from 'react';

import { ScheduleDesviationRule, ScheduleDesviationBase } from '../../repository/timekeeper/@types/ScheduleDesviationRuleModel';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useScheduleDesviationActions } from './useScheduleDesviationActions';
import { normalizeString } from 'utils/Validations';

interface Props {
    rows: ScheduleDesviationRule[];
    onClose: () => void;
    reloading: () => void;
    mode: 'add' | 'edit' | 'delete' | 'info' | 'duplicate';
    selectedRow: ScheduleDesviationRule[];
    open: boolean;
}

const EMPTY_FORM: ScheduleDesviationBase = {
    name: '',
    description: '',
    before_shift: false,
    after_shift: false,
    requires_approval: false,
    unplanned: false,
    enable: true,
};

const toFormFromScheduleDesviation = (fixedRule: ScheduleDesviationRule): ScheduleDesviationBase => ({
    name: fixedRule.name,
    description: fixedRule.description ?? '',
    before_shift: fixedRule.before_shift,
    after_shift: fixedRule.after_shift,
    requires_approval: fixedRule.requires_approval,
    unplanned: fixedRule.unplanned,
    enable: fixedRule.enable,
});

export const useScheduleDesviationForm = ({ rows, onClose, reloading, mode, selectedRow, open }: Props) => {
    const currentScheduleDesviation = mode === 'edit' || mode === 'info' || mode === 'duplicate' ? selectedRow[0] : undefined;
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { addScheduleDesviation, updateScheduleDesviation, deleteScheduleDesviations } = useScheduleDesviationActions();
    const [initialForm, setInitialForm] = useState<ScheduleDesviationBase>(EMPTY_FORM);
    const [form, setForm] = useState<ScheduleDesviationBase>(EMPTY_FORM);
    const [alert, setAlert] = useState(false);

    useEffect(() => {
        if (!open) return;

        if ((mode === 'edit' || mode === 'info' || mode === 'duplicate') && currentScheduleDesviation) {
            const preloadedForm = toFormFromScheduleDesviation(currentScheduleDesviation);
            setForm(preloadedForm);
            setInitialForm(preloadedForm);
            return;
        }
        setForm(EMPTY_FORM);
        setInitialForm(EMPTY_FORM);
    }, [open, mode, currentScheduleDesviation]);

    const update = <K extends keyof ScheduleDesviationBase>(field: K, value: ScheduleDesviationBase[K]) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = () => {
        const body = {
            ...form,
        } as unknown as ScheduleDesviationBase;

        if (mode === 'edit' && currentScheduleDesviation) {
            updateScheduleDesviation({
                setLoading: () => {},
                scheduleDesviationId: currentScheduleDesviation.schedule_desviation_rule_id,
                body,
                closing: onClose,
                reloading,
                messageError: {
                    title: 'Error',
                    message: t('settings.schedule_desviation.table.dialog.edit.messages.error'),
                    severity: 'error',
                },
                messageSuccess: {
                    title: 'Éxito',
                    message: t('settings.schedule_desviation.table.dialog.edit.messages.success'),

                    severity: 'success',
                },
                handlerAlert: (message) => {
                    enqueueSnackbar(message.message, { variant: message.severity });
                },
            });
            return;
        }

        if (mode === 'add') {
            addScheduleDesviation({
                setLoading: () => {},
                body,
                closing: onClose,
                reloading,
                messageError: {
                    title: 'Error',
                    message: t('settings.schedule_desviation.table.dialog.add.messages.error'),
                    severity: 'error',
                },
                messageSuccess: {
                    title: 'Éxito',
                    message: t('settings.schedule_desviation.table.dialog.add.messages.success'),
                    severity: 'success',
                },
                handlerAlert: (message) => {
                    enqueueSnackbar(message.message, { variant: message.severity });
                },
            });
        }

        if (mode === 'duplicate') {
            addScheduleDesviation({
                setLoading: () => {},
                body,
                closing: onClose,
                reloading,
                messageError: {
                    title: 'Error',
                    message: t('settings.schedule_desviation.table.dialog.duplicate.messages.error'),
                    severity: 'error',
                },
                messageSuccess: {
                    title: 'Éxito',
                    message: t('settings.schedule_desviation.table.dialog.duplicate.messages.success'),
                    severity: 'success',
                },
                handlerAlert: (message) => {
                    enqueueSnackbar(message.message, { variant: message.severity });
                },
            });
        }

        if (mode === 'delete') {
            deleteScheduleDesviations({
                setLoading: () => {},
                scheduleDesviationIds: selectedRow.map((row) => row.schedule_desviation_rule_id),
                closing: onClose,
                reloading,
                messageError: {
                    title: 'Error',
                    message: t('settings.schedule_desviation.table.dialog.delete.messages.error'),
                    severity: 'error',
                },
                messageSuccess: {
                    title: 'Éxito',
                    message: t('settings.schedule_desviation.table.dialog.delete.messages.success'),
                    severity: 'success',
                },
                handlerAlert: (message) => {
                    enqueueSnackbar(message.message, { variant: message.severity });
                },
            });
        }
    };

    const existName = useMemo(() => {
        return (name: string) => {
            const normalizedInput = normalizeString(name);
            return rows.some((row) => {
                if (mode === 'edit' && currentScheduleDesviation && row.schedule_desviation_rule_id === currentScheduleDesviation.schedule_desviation_rule_id) return false;
                return normalizeString(row.name) === normalizedInput;
            });
        };
    }, [rows, mode, currentScheduleDesviation]);

    const disabled = useMemo(() => {
        return form.name === '' || existName(form.name) || (!form.before_shift && !form.after_shift && !form.unplanned);
    }, [form, existName]);

    const hasChanges = useMemo(() => {
        return JSON.stringify(form) !== JSON.stringify(initialForm);
    }, [form, initialForm]);

    const cleanForm = () => {
        setForm(EMPTY_FORM);
        setInitialForm(EMPTY_FORM);
        setAlert(false);
        onClose();
    };

    const detectChanges = () => {
        if (hasChanges) {
            setAlert(true);
        } else {
            cleanForm();
        }
    };

    return {
        form,
        alert,
        update,
        disabled,
        setAlert,
        existName,
        cleanForm,
        handleSubmit,
        detectChanges,
    };
};
